import omit from "lodash/omit";

import { AbstractAnalyticsClient } from "./AbstractAnalyticsClient";
import AnalyticsLogger from "./AnalyticsLogger";
import { ZodIssue, ZodObject, ZodType, ZodVoid } from "./schema-util";
import { AnalyticsEventKey, AnalyticsEvents, AnalyticsEventsSchema } from "./types";

const isVoidType = (schema: ZodType): boolean => {
  return schema instanceof ZodVoid;
};
const isObjectType = (schema: ZodType): boolean => {
  return schema instanceof ZodObject;
};

/**
 * This base analytics client provides a common interface for tracking events and properties.
 * It is an abstract class that should be extended by a concrete implementation that implements
 * the `sendEvent` method to actually send the event to the vendor analytics service.
 */
export abstract class BaseAnalyticsClient extends AbstractAnalyticsClient {
  constructor(
    public schema: AnalyticsEventsSchema,
    public logger: AnalyticsLogger
  ) {
    super();
  }

  /**
   * Perform tracking of an analytics event. Will pre-process the event so that only allowed properties are
   * in turn sent to the analytics service.
   *
   * @param event - The event name to track
   * @param properties - The properties to track with the event
   */
  public track<T extends AnalyticsEventKey>(event: T, properties: AnalyticsEvents[T] | null = null): void {
    // Track the event
    if (!(event in this.schema.shape)) {
      // if the event is not in the schema, we don't want to track it
      this.logger.warn(event, `Event "${event}" is not defined in the analytics events schema.`);
      return;
    }
    const allowedProps = this.parsePropertiesForEvent(event, properties);
    this.logger.info(event, allowedProps);
    this.sendEvent(event, allowedProps);
  }

  private parsePropertiesForEvent<T extends AnalyticsEventKey>(
    event: T,
    properties?: AnalyticsEvents[T] | null
  ): Partial<AnalyticsEvents[T]> | undefined {
    const propsSchema = this.schema.shape[event];
    // if there is no properties schema, we can't validate
    if (!propsSchema) {
      return undefined;
    }

    // if the event schema is of type void, we don't want to send any properties
    if (isVoidType(propsSchema) && properties) {
      this.logger.warn(event, `Event schema is of type void but has properties defined.`);
      return undefined;
    }
    // if the event schema is not an object, we don't want to send any properties
    if (!isObjectType(propsSchema) && properties !== null) {
      this.logger.warn(event, `Event schema is not defined as an object.`);
      return undefined;
    }

    Object.keys(properties ?? {}).forEach((key) => {
      if (propsSchema instanceof ZodObject) {
        if (propsSchema.shape && typeof propsSchema.shape === "object" && !(key in propsSchema.shape)) {
          this.logger.info(event, `Property "${key}" is not defined in the event configuration and is filtered out.`);
        }
      }
    });

    const res = propsSchema?.safeParse(properties);
    if (res.success) {
      if (res.data && Object.keys(res.data).length === 0) {
        return undefined;
      }
      return res.data as AnalyticsEvents[T];
    }

    const propertiesToOmit: string[] = []; // a list of object paths to omit from the properties object
    // Report any errors before returning the filtered properties
    res.error.issues.forEach((error: ZodIssue) => {
      const path = error.path.join(".");
      propertiesToOmit.push(path);
      if (error.code === "invalid_type") {
        if (path === "" && error.expected === "object") {
          this.logger.warn(event, `Event "${event}" properties are not provided as an object.`);
          return;
        }
      }
      this.logger.warn(event, `"${path}": ${error.message}`, properties);
    });
    const filteredProps = omit(properties, propertiesToOmit);
    if (Object.keys(filteredProps).length === 0) {
      return undefined;
    }

    return filteredProps;
  }

  public abstract identify(identifier: string): void;

  public abstract setProfile(profile: Record<string, unknown>): void;

  public abstract alias(newId: string): void;

  public abstract reset(): void;
}
